/**
 * The ONE place that answers "is this thing commerce, and therefore absent
 * from an appliance install?"
 *
 * Operator constraints this file enforces (2026-08-18):
 *   1. Studio deploys to the APPLIANCE device first — the appliance profile
 *      is the primary verification target, not a secondary variant of cloud.
 *   2. Commerce is not needed on the appliance, and its removal must be
 *      COMPLETE, not route-deep.
 *
 * WHY A MODULE AND NOT A SET PER SURFACE. Before this file, the appliance
 * profile governed exactly two derivations — `STUDIO_ROUTE_REGISTRY`
 * (studio-route-registry.ts) and `getTemplateCatalog()` (template-loader.ts)
 * — and each carried its OWN hand-maintained exclusion list. That is how
 * `admin` stayed reachable through the first removal pass: a second list is
 * a second thing to remember. The 2026-08-18 census then measured THREE MORE
 * independent template inventories that no list covered at all:
 *
 *   - `FEATURED_TEMPLATES` (pages/homepage/data/featured-templates.ts) —
 *     21 entries, 14 of them `category: "ecommerce"`, and the backing data
 *     for the REGISTERED, INVOCABLE WebMCP tools `list_cogo_templates` and
 *     `remix_cogo_template` (mcp/register-cogo-xyz-tools.ts). Hiding a nav
 *     route does nothing to a tool an agent can still call by name.
 *   - `TEMPLATE_REGISTRY` (features/templates/registry.ts) — the chat
 *     template-proposal path, whose only entry is `kind: "storefront"` and
 *     which fires on the typed terms 빵집 / 베이커리 / bakery / 스토어프론트 /
 *     storefront.
 *   - the public marketing/payment pages (`/pricing`, `/checkout/*`,
 *     `/refund`) — subscription + N-Genius card-order screens, served
 *     WITHOUT authentication at the appliance's own origin.
 *
 * So: ONE marker vocabulary, ONE predicate, read by every surface. Each
 * inventory spells commerce in its own field (`domain` in the template
 * catalog, `category` in the featured list, `kind` in the template package),
 * but they spell it with the SAME tokens — so the tokens live here once and
 * each consumer passes its own field in. Adding a commerce token to this set
 * removes it from every inventory at once; that is the property the previous
 * per-surface lists did not have.
 *
 * Every export below reads {@link isApplianceDistribution} — the single
 * distribution predicate — and nothing else. No surface re-parses
 * `VITE_STUDIO_DISTRIBUTION` for itself.
 */
import { isApplianceDistribution } from './studio-presentation-profile'
import type { StudioRoute } from '@/store/types'

/**
 * Commerce as each template inventory spells it.
 *
 * `ecommerce` is the token used by the template catalog's `domain`
 * (@cogo/schema template-catalog.json), `FEATURED_TEMPLATES`' `category`, and
 * the block catalog's category `id` (@cogo/schema catalog-v2.json).
 * `storefront` is the token used by a `cogo.template.v1` package's `kind`
 * (features/templates/registry/*.template.json).
 * `store` is the token used by the shipped app-bundle manifest's `category`
 * (apps/cogo-starter/public/apps/_manifest.json) — a FIFTH inventory, added
 * 2026-08-18 when the build-time bundle filter began reading it. It collides
 * with no other vocabulary (measured: no catalog `domain`, no featured
 * `category`, and no block-catalog category `id` spells `store`), so adding it
 * here removes `store`-declared bundles and nothing else. That is the property
 * this single set exists to have: a new token, not a new list.
 */
export const APPLIANCE_HIDDEN_COMMERCE_MARKERS: ReadonlySet<string> = new Set([
  'ecommerce',
  'storefront',
  'store',
])

/**
 * The commerce/"Operations" route surface, measured live-reachable in
 * appliance mode by the 2026-08-18 full-feature walk
 * (`docs/superpowers/specs/2026-08-18-appliance-studio-full-walk.md` §3):
 * the Operations nav group and its sub-tabs, plus `billing` (subscription
 * management does not apply to an air-gapped appliance).
 *
 * `admin` WAS in this set and is not any more (wave 6 reconciliation,
 * 2026-08-18). The wave-5 entry was correct for the code it measured: the
 * slice-5B census
 * (`docs/superpowers/specs/2026-08-18-slice5-settings-admin.md` §6) read an
 * `admin-page.tsx` whose entire content was N-Genius card-payment
 * reconciliation, and a card-payment console has no business on an air-gapped
 * bank install. PR #2143 then SPLIT that page: `admin-page.tsx` is now a
 * two-line selector over `ApplianceAdminPage` (local identity administration
 * — invite/disable users, installation-issued password count, signup
 * open/closed; measured zero occurrences of ngenius/payment/refund/unassigned
 * across its 329 lines) and `CloudPaymentsAdminPage` (the original N-Genius
 * console). The premise that justified hiding the ROUTE no longer holds, and
 * hiding it made the appliance's own identity-administration screen
 * unreachable on the only distribution it was written for.
 *
 * The property wave 5 protected did not go away — it MOVED from the route
 * layer to the content layer, where the split now enforces it, and it is
 * asserted there by
 * `src/lib/__tests__/studio-appliance-admin-surface.test.tsx`: on an
 * appliance `/studio/admin` resolves, renders the identity console, and
 * reaches no payments surface. Deleting `'admin'` from this set without that
 * content-layer gate would be a removal of the guarantee, not a relocation
 * of it.
 *
 * Route ids are not markers (a route is not "an ecommerce-domain thing"), so
 * this stays its own set — but it lives HERE, beside the markers, so that a
 * future removal has one file to edit rather than one file per surface.
 */
export const APPLIANCE_HIDDEN_ROUTES: ReadonlySet<StudioRoute> = new Set<StudioRoute>([
  'commerce',
  'products',
  'orders',
  'revenue',
  'members',
  'billing',
  'analytics',
])

/**
 * Public (unauthenticated) paths whose entire content is commerce: the
 * subscription price list and the three N-Genius card-order outcome screens
 * it hands off to, plus the refund policy that exists only to describe those
 * charges.
 *
 * These are NOT `/studio/*` routes, so `APPLIANCE_HIDDEN_ROUTES` never saw
 * them — App.tsx serves them from its own `INFO_PAGES` table before the auth
 * gate. On an appliance they are reachable at the box's own origin by anyone
 * who can load the page, signed in or not, which also contradicts operator
 * constraint 3 ("Studio login is the first screen").
 */
export const APPLIANCE_HIDDEN_PUBLIC_PATHS: ReadonlySet<string> = new Set([
  '/pricing',
  '/checkout/success',
  '/checkout/failure',
  '/checkout/pending',
  '/refund',
])

/**
 * True when the caller's inventory row is commerce AND this build is an
 * appliance — i.e. when the row must not reach the user.
 *
 * Callers pass whichever field their own inventory uses to declare a row's
 * kind (`domain` / `category` / `kind`). A row with no such field is never
 * hidden: absence of a commerce declaration is not a commerce declaration.
 */
export function applianceHidesCommerceMarker(marker: string | undefined | null): boolean {
  if (!marker) return false
  if (!isApplianceDistribution()) return false
  return APPLIANCE_HIDDEN_COMMERCE_MARKERS.has(marker)
}

/** True when `route` must not resolve or appear in nav on this build. */
export function applianceHidesRoute(route: StudioRoute): boolean {
  return isApplianceDistribution() && APPLIANCE_HIDDEN_ROUTES.has(route)
}

/** True when `path` (a public, pre-auth URL) must not render on this build. */
export function applianceHidesPublicPath(path: string): boolean {
  return isApplianceDistribution() && APPLIANCE_HIDDEN_PUBLIC_PATHS.has(path)
}

// ─────────────────────────────────────────────────────────────────────────
// Build-time: which shipped app bundles reach the appliance dist at all.
//
// The runtime gates above decide what a person can SEE and what Studio can
// LOAD. Neither touches BYTES: `copyStaticAssetsPlugin` (vite.config.ts)
// `cpSync`s every directory under `apps/cogo-starter/public/apps` into
// `dist/apps`, so `GET /apps/e-commerce-new3/app.json` answered on an
// appliance even after `loadTemplateWithBundle` began refusing that id.
// Removal that stops at the loader is removal that stops at the door.
//
// Runs in NODE (a Vite plugin), not in the browser — hence the declarations
// are passed IN rather than imported, and the distribution is read through
// the same `isApplianceDistribution(env)` every other surface uses, with the
// build's resolved env handed to it.
// ─────────────────────────────────────────────────────────────────────────

/**
 * What the two shipping authorities declare about each bundle directory.
 *
 * A bundle's kind is declared in two places and NOWHERE else — measured
 * 2026-08-18 by reading `app.json` for all 66 directories: its top-level keys
 * are `appId,name,version,schemaVersion,description,theme,…` with no domain,
 * category, or kind field. So there is no per-bundle self-declaration to
 * derive from; these two are the authorities that exist:
 *
 *   - `catalogTemplates` — `@cogo/schema/template-catalog.json`, `domain`
 *     (47 rows). The SAME field `getTemplateCatalog()` and
 *     `loadTemplateWithBundle()` read, so the bytes and the loader cannot
 *     disagree about a given id.
 *   - `manifestApps` — `apps/cogo-starter/public/apps/_manifest.json`,
 *     `category` (55 rows). Ships beside the bytes and covers ids the
 *     catalogue never listed, including the pre-`-new3` duplicates
 *     (`coffee-shop-store`, `pet-supply-store`).
 */
export interface ApplianceAppBundleDeclarations {
  catalogTemplates: ReadonlyArray<{ id: string; domain?: string }>
  manifestApps: ReadonlyArray<{ id: string; category?: string }>
}

/**
 * The subset of `bundleIds` that must NOT be copied into an appliance dist.
 *
 * Empty on any non-appliance build — the cloud dist is byte-identical to what
 * it shipped before this filter existed.
 *
 * A bundle is excluded when EITHER authority declares it with a marker from
 * {@link APPLIANCE_HIDDEN_COMMERCE_MARKERS}. A bundle that neither authority
 * declares is RETAINED: absence of a declaration is not a declaration, and
 * inventing a list of ids to make up the difference is the second hand-list
 * this module exists to avoid. The undeclared residual is named in
 * `docs/superpowers/specs/2026-08-18-appliance-first-enforcement.md` §5 so it
 * is decided rather than forgotten.
 */
export function applianceExcludedAppBundleIds(
  bundleIds: readonly string[],
  declarations: ApplianceAppBundleDeclarations,
  env?: Partial<Pick<ImportMetaEnv, 'VITE_STUDIO_DISTRIBUTION'>>,
): string[] {
  if (!isApplianceDistribution(env)) return []
  const domainById = new Map(declarations.catalogTemplates.map((t) => [t.id, t.domain]))
  const categoryById = new Map(declarations.manifestApps.map((a) => [a.id, a.category]))
  return bundleIds.filter((id) => {
    const declared = [domainById.get(id), categoryById.get(id)]
    return declared.some(
      (marker) => Boolean(marker) && APPLIANCE_HIDDEN_COMMERCE_MARKERS.has(marker as string),
    )
  })
}

/**
 * `bundleIds` split into what this build ships and what it withholds.
 *
 * Returned as a partition (not just the exclusions) because the gate must
 * assert BOTH directions from the same call — that no commerce bundle is in
 * `retained` AND that the non-commerce bundles still are. An exclusion list
 * alone cannot fail the second way, and a filter that dropped everything
 * would satisfy it.
 */
export function partitionApplianceAppBundles(
  bundleIds: readonly string[],
  declarations: ApplianceAppBundleDeclarations,
  env?: Partial<Pick<ImportMetaEnv, 'VITE_STUDIO_DISTRIBUTION'>>,
): { excluded: string[]; retained: string[] } {
  const excluded = applianceExcludedAppBundleIds(bundleIds, declarations, env)
  const excludedSet = new Set(excluded)
  return { excluded, retained: bundleIds.filter((id) => !excludedSet.has(id)) }
}
